/**
 * 💚 ONLINE TAB - Architecture Mozart/Jobs/Tesla
 * ⚡ VERSION TEMPS RÉEL avec WebSocket
 * 
 * L'onglet des utilisateurs en ligne / au magasin.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Circle, Clock, MessageCircle, Heart, Wifi, WifiOff } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OnlineTab = ({ customerId, isDarkMode, onOpenChat, onSendCrush, socket, isConnected }) => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [atStoreUsers, setAtStoreUsers] = useState([]);
  const [filter, setFilter] = useState('all'); // all, online, store
  const [isLoading, setIsLoading] = useState(false);

  const loadOnlineUsers = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/heartbeats/online/${customerId}`);
      const data = await response.json();
      if (data.success) {
        setOnlineUsers(data.online || []);
        setAtStoreUsers(data.atStore || []);
      }
    } catch (err) {
      console.error('Error loading online users:', err);
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadOnlineUsers();
  }, [loadOnlineUsers]);

  // ⚡ TEMPS RÉEL : Écouter les événements WebSocket
  useEffect(() => {
    if (!socket || !isConnected) {
      // Fallback polling si pas de WebSocket
      const interval = setInterval(loadOnlineUsers, 20000);
      return () => clearInterval(interval);
    }
    
    // Écouter les mises à jour temps réel
    const handleOnlineUsersUpdate = (data) => {
      console.log('⚡ Online users updated via WebSocket');
      loadOnlineUsers(); // Recharger les données complètes
    };
    
    const handleUserOnline = (data) => {
      console.log('👋 User came online:', data.customerId);
      if (data.customerId !== customerId) {
        loadOnlineUsers();
      }
    };
    
    const handleUserOffline = (data) => {
      console.log('👋 User went offline:', data.customerId);
      setOnlineUsers(prev => prev.filter(u => u.id !== data.customerId));
    };
    
    const handleAtStoreUpdate = (data) => {
      console.log('📍 At store updated');
      setAtStoreUsers(data.usersAtStore || []);
    };
    
    const handleUserAtStore = (data) => {
      console.log('📍 User arrived at store:', data.customerId);
      if (data.customerId !== customerId) {
        toast.info('📍 Quelqu\'un vient d\'arriver au magasin !');
        loadOnlineUsers();
      }
    };
    
    socket.on('online_users_updated', handleOnlineUsersUpdate);
    socket.on('user_online', handleUserOnline);
    socket.on('user_offline', handleUserOffline);
    socket.on('at_store_updated', handleAtStoreUpdate);
    socket.on('user_at_store', handleUserAtStore);
    
    return () => {
      socket.off('online_users_updated', handleOnlineUsersUpdate);
      socket.off('user_online', handleUserOnline);
      socket.off('user_offline', handleUserOffline);
      socket.off('at_store_updated', handleAtStoreUpdate);
      socket.off('user_at_store', handleUserAtStore);
    };
  }, [socket, isConnected, customerId, loadOnlineUsers]);

  const displayedUsers = filter === 'store' 
    ? atStoreUsers 
    : filter === 'online' 
      ? onlineUsers 
      : [...atStoreUsers, ...onlineUsers.filter(u => !atStoreUsers.find(s => s.id === u.id))];

  return (
    <div className={`space-y-6 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <div className="text-center">
        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          🟢 En ligne
        </h2>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Qui est disponible maintenant ?
        </p>
        {/* Indicateur temps réel */}
        <div className={`flex items-center justify-center gap-2 text-xs mt-2 ${isConnected ? 'text-green-400' : 'text-yellow-400'}`}>
          {isConnected ? (
            <>
              <Wifi className="w-3 h-3" />
              <span>Temps réel</span>
            </>
          ) : (
            <>
              <WifiOff className="w-3 h-3" />
              <span>Actualisation toutes les 20s</span>
            </>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="flex justify-center gap-2">
        {[
          { id: 'all', label: 'Tous', count: onlineUsers.length },
          { id: 'store', label: '🏪 Au magasin', count: atStoreUsers.length },
          { id: 'online', label: '🟢 En ligne', count: onlineUsers.length }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              filter === f.id
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                : isDarkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
            {f.count > 0 && (
              <span className="ml-1 opacity-70">({f.count})</span>
            )}
          </button>
        ))}
      </div>

      {/* Au magasin banner */}
      {atStoreUsers.length > 0 && filter !== 'online' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-2xl ${
            isDarkMode 
              ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700/30' 
              : 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <MapPin className="w-5 h-5 text-green-500 animate-pulse" />
            <span className={`font-bold ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
              {atStoreUsers.length} personne{atStoreUsers.length > 1 ? 's' : ''} au magasin !
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            L'occasion parfaite de faire connaissance en vrai 😊
          </p>
        </motion.div>
      )}

      {/* Liste */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin text-4xl">🟢</div>
        </div>
      ) : displayedUsers.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-5xl mb-4">😴</div>
          <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Personne en ligne
          </h3>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            Reviens plus tard !
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {displayedUsers.map((user, index) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className={`p-4 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gray-800/70 border border-gray-700' 
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}
            >
              {/* Avatar */}
              <div className="relative mx-auto w-20 h-20 mb-3">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-3xl">
                  {user.avatar || '😊'}
                </div>
                <div className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 ${
                  user.atStore 
                    ? 'bg-green-500 border-green-300' 
                    : 'bg-green-400 border-green-200'
                }`}>
                  {isConnected && (
                    <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75" />
                  )}
                </div>
              </div>

              {/* Info */}
              <h4 className={`font-bold text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {user.name}, {user.age}
              </h4>
              
              {user.atStore && (
                <p className="text-center text-xs text-green-500 flex items-center justify-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  Au magasin
                </p>
              )}

              {/* Actions */}
              <div className="flex justify-center gap-2 mt-3">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onSendCrush?.(user)}
                  className="text-pink-500 hover:bg-pink-50"
                >
                  <Heart className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onOpenChat?.(user)}
                  className="text-purple-500 hover:bg-purple-50"
                >
                  <MessageCircle className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OnlineTab;
